import React, { useMemo, useState } from 'react';
import { alpha, Box, Button, Checkbox, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { GREEN, MONO } from './scanUi';

// Searchable, scrollable host list with Select all / Clear. `isPicked(id)` and `onToggle(id)` come from the page;
// `onSetMany(ids)` replaces the selection (used by Select all and Clear).
export default function ScopePicker({ rows, isPicked, onToggle, onSetMany, selectedCount }) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => r.value.toLowerCase().includes(s) || String(r.kind || '').toLowerCase().includes(s)) : rows;
  }, [rows, q]);
  const allShownPicked = shown.length > 0 && shown.every((r) => isPicked(r.id));

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <TextField size="small" fullWidth value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${rows.length} addresses`}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
        <Typography variant="caption" color="text.secondary">{selectedCount} selected{q ? ` · ${shown.length} match` : ''}</Typography>
        <Box>
          <Button size="small" disabled={!shown.length || allShownPicked} onClick={() => onSetMany([...new Set([...rows.filter((r) => isPicked(r.id)).map((r) => r.id), ...shown.map((r) => r.id)])])} sx={{ color: GREEN, fontWeight: 700, minWidth: 0 }}>
            Select {q ? `these ${shown.length}` : `all ${rows.length}`}
          </Button>
          <Button size="small" onClick={() => onSetMany([])} sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 0 }}>Clear</Button>
        </Box>
      </Stack>
      <Stack gap={0.7} sx={{ maxHeight: 340, overflowY: 'auto', pr: 0.5 }}>
        {shown.length === 0 && <Typography variant="body2" color="text.secondary">Nothing matches.</Typography>}
        {shown.map((r) => (
          <Stack key={r.id} direction="row" alignItems="center" onClick={() => onToggle(r.id)}
            sx={{ pr: 1.5, borderRadius: 2.5, cursor: 'pointer', flexShrink: 0, border: 1, borderColor: isPicked(r.id) ? GREEN : 'divider', bgcolor: isPicked(r.id) ? alpha(GREEN, 0.06) : 'transparent' }}>
            <Checkbox size="small" checked={isPicked(r.id)} tabIndex={-1} inputProps={{ 'aria-label': `Scan ${r.value}` }} sx={{ '&.Mui-checked': { color: GREEN } }} />
            <Typography noWrap sx={{ flex: 1, fontFamily: MONO, fontSize: 13 }}>{r.value}</Typography>
            <Typography variant="caption" color="text.secondary">{r.kind || r.kindText}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
